import {useEffect, useState} from "react";
import _ from "lodash";
import {useAtomValue, useSetAtom} from "jotai";
import {useTranslation} from "react-i18next";
import {Box, Button, Stack, Typography} from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {useResourceManagement, useResourceValueOrDefault} from "../../hooks/resourceHooks";
import {swallowException} from "../../utils/appUtils";
import {EditIcon} from "../icons/EditIcon";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {activeOrderAtom, activeOrderLoadable} from "../../store/activeOrderStore";
import {OrderCreateDialog} from "../dialogs/OrderCreateDialog";
import {OrderDetailsCard} from "./OrderDetailsCard";
import {getCompanyById} from "../../services/companyService";
import {getUserById} from "../../services/userService";
import {OrderFlagsCard} from "./OrderFlagsCard";
import {OrderFlagsEditDialog} from "../dialogs/OrderFlagsEditDialog";
import {useOrderManagement} from "../../hooks/ordersHooks";
import {MainViewLayout} from "../MainViewLayout";
import {OrderHeader} from "./OrderHeader";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {ErrorViewLayout} from "../ErrorViewLayout";

export const OrderDetailsView: React.FC = () => {
    const {t} = useTranslation();
    const [orderEditDialogOpened, setOrderEditDialogOpened] = useState<boolean>(false);
    const [orderFlagsDialogOpened, setOrderFlagsDialogOpened] = useState<boolean>(false);
    const orderResource = useAtomValue(activeOrderLoadable);
    const order = useResourceValueOrDefault(orderResource, undefined);
    const refreshOrder = useSetAtom(activeOrderAtom);
    const {getCompanyById: getCompany, resource: companyResource} = useResourceManagement({getCompanyById});
    const company = useResourceValueOrDefault(companyResource, undefined);
    const {getUserById: getUser, resource: userResource} = useResourceManagement({getUserById});
    const user = useResourceValueOrDefault(userResource, undefined);
    const orderManagement = useOrderManagement();

    useEffect(() => {
        if (order !== undefined) {
            getCompany(order.companyId).catch(swallowException);
            getUser(order.userId).catch(swallowException);
        }
    }, [getCompany, getUser, order]);

    const access = useAccessMarker(ACCESS_FUNCTIONS);

    const downloadShapeFile = () => {
        orderManagement.downloadShapeFile(order!.companyId, order!.id).catch(swallowException);
    };

    const onOrderEditDialogClose = (isSuccessful: boolean) => {
        setOrderEditDialogOpened(false);
        if (isSuccessful) {
            refreshOrder();
        }
    };

    const onOrderFlagsDialogClose = (isSuccessful: boolean) => {
        setOrderFlagsDialogOpened(false);
        if (isSuccessful) {
            refreshOrder();
        }
    };

    if (isResourceFailed(orderResource)) {
        return <ErrorViewLayout errorMessage={getResourceErrorMessage(orderResource)} />;
    } else {
        return (
            <MainViewLayout header={<OrderHeader orderResource={orderResource} />}>
                <Stack direction="column" gap="1.5rem">
                    <Stack direction="column">
                        <Stack
                            direction="row"
                            height="4rem"
                            alignItems="center"
                            justifyContent="space-between"
                            gap="1rem"
                        >
                            <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                {t("orders.order_detail_view.details")}
                            </Typography>
                            <Stack direction="row" gap="1rem">
                                <Button
                                    startIcon={
                                        <DownloadIcon
                                            sx={{
                                                color: "#303A8C",
                                                width: "1.5rem",
                                                height: "1.5rem",
                                            }}
                                        />
                                    }
                                    onClick={downloadShapeFile}
                                >
                                    {t("orders.order_detail_view.download_shapefile")}
                                </Button>
                                {access.canEditAnyOrder && (
                                    <Button
                                        startIcon={
                                            <EditIcon
                                                sx={{
                                                    stroke: "#303A8C",
                                                    width: "1.125rem",
                                                    height: "1.125rem",
                                                }}
                                            />
                                        }
                                        onClick={() => setOrderEditDialogOpened(true)}
                                    >
                                        {t("orders.order_detail_view.edit_details")}
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                        <Box padding="1.5rem" bgcolor="white">
                            <OrderDetailsCard company={company} order={order} user={user} />
                        </Box>
                    </Stack>
                    <Stack direction="column">
                        <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                            <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                {t("orders.order_detail_view.flags")}
                            </Typography>
                            {access.canEditAnyOrder && (
                                <Button
                                    startIcon={
                                        <EditIcon
                                            sx={{
                                                stroke: "#303A8C",
                                                width: "1.125rem",
                                                height: "1.125rem",
                                            }}
                                        />
                                    }
                                    onClick={() => setOrderFlagsDialogOpened(true)}
                                >
                                    {t("orders.order_detail_view.edit_flags")}
                                </Button>
                            )}
                        </Stack>
                        <Box padding="1.5rem" bgcolor="white">
                            <OrderFlagsCard order={order} />
                        </Box>
                    </Stack>
                </Stack>
                {access.canEditAnyOrder && orderEditDialogOpened && order !== undefined && (
                    <OrderCreateDialog
                        mode="edit"
                        companyId={order.companyId}
                        selectedOrder={order}
                        open={orderEditDialogOpened}
                        onClose={onOrderEditDialogClose}
                    />
                )}
                {access.canEditAnyOrder && orderFlagsDialogOpened && order !== undefined && (
                    <OrderFlagsEditDialog
                        companyId={order.companyId}
                        order={order}
                        open={orderFlagsDialogOpened}
                        onClose={onOrderFlagsDialogClose}
                    />
                )}
            </MainViewLayout>
        );
    }
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canEditAnyOrder"]);
